import {
  Dialog,
  DialogBackdrop,
  Tab,
  TabGroup,
  TabList,
} from '@headlessui/react'
import {
  Link,
  Outlet,
  useLocation,
  useParams,
  useRouteLoaderData,
} from '@remix-run/react'
import { Fragment, useEffect, useState } from 'react'
import { IconRedArrowRight, IconShare } from '~/components/Icons'
import RedIcon from '~/components/RedIcon'
import type { loader } from '~/routes/report/v2/layout'
import BookingPopup from './components/BookingPopup'
import { CostSummary } from './components/CostSummary'
import TabTitle from './components/TabTitle'

export default function ReportPage() {
  const { id } = useParams()
  const location = useLocation()

  const data = useRouteLoaderData<typeof loader>('routes/report/v2/layout')
  if (!data) return null

  const { report } = data
  const [selectedTab, setSelectedTab] = useState(0)

  const [copied, setCopied] = useState(false)

  const copyToClipboard = () => {
    const url = new URL(window.location.href)
    const match = RegExp(/\/report\/v2\/[^/]+/).exec(url.pathname)
    if (match) {
      const baseUrl = `${url.origin}${match[0]}`
      navigator.clipboard.writeText(baseUrl).then(() => {
        setCopied(true)
        setTimeout(() => {
          setCopied(false)
        }, 1000)
      })
    }
  }

  useEffect(() => {
    const path = location.pathname.split('/')
    const tabIndex = path[path.length - 1] || path[path.length - 2]
    switch (tabIndex) {
      case '':
        setSelectedTab(0)
        break
      case 'lounge':
        setSelectedTab(1)
        break
      case 'cabin':
        setSelectedTab(2)
        break
      case 'feedback':
        setSelectedTab(3)
        break
      case 'faqs':
        setSelectedTab(4)
        break
      default:
        setSelectedTab(0)
        break
    }
  }, [location])

  return (
    <section className="not-prose text-neutral-800">
      <div className="flex justify-between text-lg leading-7 font-bold mb-1">
        <h2 className="text-h2">{report.reportSummary.reportTitle}</h2>
        <button
          type="button"
          onClick={copyToClipboard}
          style={{ cursor: 'pointer' }}
        >
          <IconShare />
        </button>

        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          open={copied}
          onClose={() => setCopied(false)}
        >
          <div className="min-h-screen px-4 text-center">
            <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50" />

            {/* Centering the content */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="inline-block p-6 my-8 overflow-hidden align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <p className="text-center">Link copied</p>
            </div>
          </div>
        </Dialog>
      </div>
      <p className="text-neutral-600 not-prose">
        {Intl.DateTimeFormat('default', {
          day: 'numeric',
          month: 'long',
        }).format(new Date(report.reportSummary.fromDate))}{' '}
        -{' '}
        {Intl.DateTimeFormat('default', {
          day: 'numeric',
          month: 'long',
        }).format(new Date(report.reportSummary.toDate))}{' '}
        ・{' '}
        {report.reportSummary.numOfSeats === 1 ? (
          <span>{report.reportSummary.numOfSeats} seat</span>
        ) : (
          <span>{report.reportSummary.numOfSeats} seats</span>
        )}
        {report.reportSummary.typeOfFlight
          ? `・ ${report.reportSummary.typeOfFlight}`
          : ''}
      </p>
      <hr className="my-4" />
      <h3 className="text-h3 font-bold mb-1">Total costs</h3>

      <CostSummary summary={report.costSummary} />

      <Link
        to="info"
        className="w-full flex gap-2 p-4 border border-neutral-300 rounded-xl items-center my-8 cursor-pointer no-underline"
      >
        <RedIcon iconsvg="info" />
        <div className="flex flex-col items-start">
          <p className="md:text-p text-xs font-semibold m-0">
            Read before booking
          </p>
          <p className="md:text-p text-xs m-0 text-neutral-600 font-normal">
            Important information for your trip
          </p>
        </div>
        <div className="ml-auto">
          <IconRedArrowRight />
        </div>
      </Link>

      <TabGroup selectedIndex={selectedTab} onChange={setSelectedTab}>
        <TabList className="flex space-x-1 border-b border-neutral-300 my-8 justify-evenly">
          <Tab as={Fragment}>
            {({ selected }) => (
              <Link
                to={`/report/v2/${id}`}
                preventScrollReset
                className={`border-b-2 no-underline ${
                  selected
                    ? 'opacity-100 border-neutral-800'
                    : 'opacity-30 border-white'
                }`}
              >
                <TabTitle iconsvg="ticket" title="Itinerary" />
              </Link>
            )}
          </Tab>
          <Tab as={Fragment}>
            {({ selected }) => (
              <Link
                to="lounge"
                preventScrollReset
                className={`border-b-2 no-underline ${
                  selected
                    ? 'opacity-100 border-neutral-800'
                    : 'opacity-30 border-white'
                }`}
              >
                <TabTitle iconsvg="lounge" title="Lounge" />
              </Link>
            )}
          </Tab>
          <Tab as={Fragment}>
            {({ selected }) => (
              <Link
                to="cabin"
                preventScrollReset
                className={`border-b-2 no-underline ${
                  selected
                    ? 'opacity-100 border-neutral-800'
                    : 'opacity-30 border-white'
                }`}
              >
                <TabTitle iconsvg="cabin" title="Cabin" />
              </Link>
            )}
          </Tab>
          <Tab as={Fragment}>
            {({ selected }) => (
              <Link
                to="feedback"
                preventScrollReset
                className={`border-b-2 no-underline ${
                  selected
                    ? 'opacity-100 border-neutral-800'
                    : 'opacity-30 border-white'
                }`}
              >
                <TabTitle iconsvg="tips" title="Tips" />
              </Link>
            )}
          </Tab>
          <Tab as={Fragment}>
            {({ selected }) => (
              <Link
                to="faqs"
                preventScrollReset
                className={`border-b-2 no-underline ${
                  selected
                    ? 'opacity-100 border-neutral-800'
                    : 'opacity-30 border-white'
                }`}
              >
                <TabTitle iconsvg="faqs" title="FAQs" />
              </Link>
            )}
          </Tab>
        </TabList>
      </TabGroup>
      <Outlet />
      <BookingPopup />
    </section>
  )
}
